import { HttpStatusCode } from '@sonar-software/sonar-http';
import { SafeArray } from '@sonar-software/sonar-ts-types';
import { SonarState } from '@app';
import {
	AsyncState,
	AsyncStateStatus,
	isErrored,
	isFailed,
	isIdle,
	isReceived,
	isRequested,
	ResultsCollection
} from '@sonar-software/xuder';

export function createSonarSelector<T>(selector: (state: SonarState) => T): (state: SonarState) => T {
	return selector;
}

export type AsyncStateSelector<T> = (state: SonarState) => AsyncState<T>;
export type StatusSelector = (state: SonarState) => AsyncStateStatus;
export type StatusCodeSelector = (state: SonarState) => HttpStatusCode | undefined;
export type DataSelector<T> = (state: SonarState) => T | null | undefined;
export type CollectionSelector<T> = (state: SonarState) => SafeArray<T> | Array<T> | undefined;
export type BooleanSelector = (state: SonarState) => boolean;

export function asyncStateStatusSelector<T>(selector: AsyncStateSelector<T>): StatusSelector {
	return (state) => selector(state).actionStatus;
}

export function asyncStateStatusCodeSelector<T>(selector: AsyncStateSelector<T>): StatusCodeSelector {
	return (state) => selector(state).statusCode;
}

export function createDataSelector<T>(selector: AsyncStateSelector<T>): DataSelector<T> {
	return createSonarSelector((state) => selector(state).data);
}

export function createDataExistsSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	return createSonarSelector((state) => Boolean(selector(state).data));
}

export function createStatusSelector<T>(selector: AsyncStateSelector<T>): StatusSelector {
	return createSonarSelector(asyncStateStatusSelector(selector));
}

export function createStatusCodeSelector<T>(selector: AsyncStateSelector<T>): StatusCodeSelector {
	return createSonarSelector(asyncStateStatusCodeSelector(selector));
}

export function createCollectionSelector<T>(selector: DataSelector<ResultsCollection<T>>): CollectionSelector<T> {
	return createSonarSelector((state) => selector(state)?.results);
}

export function createIsIdleSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	return createSonarSelector((state) => isIdle(selector(state)));
}

export function createIsRequestedSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	return createSonarSelector((state) => isRequested(selector(state)));
}

export function createIsReceivedSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	return createSonarSelector((state) => isReceived(selector(state)));
}

export function createIsErroredSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	return createSonarSelector((state) => isErrored(selector(state)));
}

export function createIsFailedSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	return createSonarSelector((state) => isFailed(selector(state)));
}

export function createIsErroredOrFailedSelector<T>(selector: AsyncStateSelector<T>): BooleanSelector {
	const erroredSelector = createIsErroredSelector(selector);
	const failedSelector = createIsFailedSelector(selector);

	return (state) => erroredSelector(state) || failedSelector(state);
}

export interface AsyncSelectors<T> {
	data: DataSelector<T>;
	dataExists: BooleanSelector;
	isErrored: BooleanSelector;
	isFailed: BooleanSelector;
	isIdle: BooleanSelector;
	isReceived: BooleanSelector;
	isRequested: BooleanSelector;
	state: AsyncStateSelector<T>;
	status: StatusSelector;
	statusCode: StatusCodeSelector;
}

export function createAsyncSelectors<T>(selector: AsyncStateSelector<T>): AsyncSelectors<T> {
	return {
		data: createDataSelector(selector),
		dataExists: createDataExistsSelector(selector),
		isErrored: createIsErroredSelector(selector),
		isFailed: createIsFailedSelector(selector),
		isIdle: createIsIdleSelector(selector),
		isReceived: createIsReceivedSelector(selector),
		isRequested: createIsRequestedSelector(selector),
		state: selector,
		status: createStatusSelector(selector),
		statusCode: createStatusCodeSelector(selector)
	};
}
